import { useState } from "react";
import { ghostBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import type { QcAwaitingRow } from "@/lib/manufacturing/qcDashboardClient";
import { confirmBuilderAssistantSession } from "@/lib/manufacturing/builderAssistantClient";
import { queueBuilderAssistantSession } from "@/lib/manufacturing/builderAssistantOpenSession";
import { requestWorkspaceMode } from "@/lib/workspaceModeNavigation";
import { QcDashboardFold } from "./QcDashboardFold";
import { QcReportDeleteButton } from "./QcReportDeleteButton";
import { QcListFilter, QcListPager, useQcListControls } from "./qcListControls";

function openSession(sessionId: string) {
  queueBuilderAssistantSession(sessionId);
  requestWorkspaceMode({ mode: "mfgBuilderAssistant" });
}

export function QcDashboardAwaiting({ rows, onDeleted }: { rows: QcAwaitingRow[]; onDeleted: () => void }) {
  const [error, setError] = useState<string | null>(null);
  const list = useQcListControls(rows, (row, query) =>
    [row.serial, row.model, row.size, row.stage].join(" ").toLowerCase().includes(query),
  );

  async function inspect(row: QcAwaitingRow) {
    setError(null);
    if (row.session_id) {
      openSession(row.session_id);
      return;
    }
    if (!row.unit_id) return;
    const started = await confirmBuilderAssistantSession(row.unit_id, row.model);
    if (!started.ok) {
      setError(started.error);
      return;
    }
    openSession(started.data.session.id);
  }

  return (
    <QcDashboardFold title="Awaiting QC" defaultOpen>
      <p className="text-sm text-gray-900 dark:text-zinc-100">
        Open inspections, units in Final QA, and ShedSuite buildings that are still at the plant with no QC session.
      </p>
      <QcListFilter value={list.query} onChange={list.setQuery} placeholder="Filter serial, model, or stage" />
      {error ? <p className="text-sm text-red-700 dark:text-red-300">{error}</p> : null}
      {list.total === 0 ? <p className="text-sm text-gray-900 dark:text-zinc-100">Nothing is waiting.</p> : null}
      <ul className="space-y-2">
        {list.rows.map((row) => (
          <li key={row.key} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 dark:border-zinc-700 dark:text-zinc-100">
            <span className="min-w-0">
              <span className="font-medium">{row.serial || "No serial"}</span>
              {row.model ? <span> · {row.model}</span> : null}
              {row.size ? <span> · {row.size}</span> : null}
            </span>
            <span className="contents">
              <span>{row.stage}</span>
              {row.session_id || row.unit_id ? (
                <button type="button" className={ghostBtnClass} onClick={() => void inspect(row)}>
                  {row.session_id ? "Open" : "Inspect"}
                </button>
              ) : null}
              {row.session_id ? (
                <QcReportDeleteButton
                  sessionId={row.session_id}
                  label={row.serial || "this QC report"}
                  onDeleted={onDeleted}
                />
              ) : null}
            </span>
          </li>
        ))}
      </ul>
      <QcListPager page={list.page} pages={list.pages} onPage={list.setPage} />
    </QcDashboardFold>
  );
}
